import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Store, Truck, TrendingUp, BarChart3, Megaphone, MapPin,
  Handshake, Gift, Headphones, Target, ArrowRight, X, Sparkles,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';

// Real point-of-sale photos from existing Olion partners.
// Add new photos here — page handles any count from 4 to 20 gracefully.
const POS_PHOTOS = [
  {
    // Néroli storefront with Olion blue poster + flag — flagship hero photo
    src: 'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/i3js6hu1_499044113_122233172330197143_5726718291300387393_n.jpg',
    captionFr: 'Néroli — Parfum & Détergent · partenaire Olion premium',
    captionEn: 'Néroli — Perfume & Detergent · premium Olion partner',
    captionAr: 'نيرولي — عطر ومنظفات · شريك أوليون مميّز',
    city: 'Sfax',
    span: 'lg:col-span-2 lg:row-span-2',
  },
  {
    // Olion-branded storefront with full blue banner
    src: 'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/vj5ledeg_577177469_122261429306197143_831102990870537279_n.jpg',
    captionFr: 'Façade aux couleurs Olion — visibilité maximale',
    captionEn: 'Olion-branded storefront — maximum visibility',
    captionAr: 'واجهة بألوان أوليون — رؤية قصوى',
    city: 'Sousse',
  },
  {
    // Olion store with A-frame sign + double signage
    src: 'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/aotsqhog_586268655_122263721558197143_670457719538884450_n.jpg',
    captionFr: 'Point de vente partenaire avec PLV complet fournie par Olion',
    captionEn: 'Partner POS with full point-of-sale display provided by Olion',
    captionAr: 'نقطة بيع شريك مع شاشة عرض كاملة مقدمة من أوليون',
    city: 'Tunis',
    span: 'lg:col-span-2',
  },
  {
    // Olion Vrac dispensers (colorful drums with taps)
    src: 'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/iq3gvzm9_587028283_122263721600197143_6905464039617150817_n.jpg',
    captionFr: 'Station Olion Vrac — vente au litre, marges optimisées',
    captionEn: 'Olion Bulk station — sell per liter, optimised margins',
    captionAr: 'محطة أوليون بالجملة — البيع باللتر، هوامش محسّنة',
    city: 'Monastir',
  },
  {
    // Interior shelves stocked with full Olion range
    src: 'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/3jxjt26v_Gemini_Generated_Image_9jjbzj9jjbzj9jjb.png',
    captionFr: 'Rayons partenaires garnis de la gamme Olion complète',
    captionEn: 'Partner shelves fully stocked with the Olion range',
    captionAr: 'رفوف الشركاء مزودة بمجموعة أوليون الكاملة',
    city: 'Nabeul',
  },
  {
    // Wide shop interior — full Olion vrac stations + retail shelves
    src: 'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/hzi54fsa_722097268_122294495198197143_4908935243929107434_n.jpg',
    captionFr: 'Boutique partenaire — vrac et retail dans un même espace',
    captionEn: 'Partner shop — bulk and retail in one space',
    captionAr: 'متجر شريك — البيع بالجملة والتجزئة في فضاء واحد',
    city: 'Mahdia',
    span: 'lg:col-span-2',
  },
  {
    // Olion bulk dispensing stations with branded blue banners
    src: 'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/b4j0z7xy_527778672_122247450800197143_4319857802226348370_n.jpg',
    captionFr: 'Stations Olion Vrac brandées — agencement standardisé',
    captionEn: 'Branded Olion Bulk stations — standardised layout',
    captionAr: 'محطات أوليون بالجملة موسومة — تصميم موحّد',
    city: 'Kairouan',
  },
  {
    // Shop with Olion bulk dispensers + shelves of branded packaged products
    src: 'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/gujxj907_516893250_122242394528197143_6067360003385641022_n.jpg',
    captionFr: "Présentoirs Olion en boutique — gamme premium mise en valeur",
    captionEn: 'Olion in-store displays — premium range showcased',
    captionAr: 'عروض أوليون داخل المتجر — تسليط الضوء على المجموعة الفاخرة',
    city: 'Bizerte',
  },
  {
    // Retail shelves featuring Olion packaged liquids + sprays
    src: 'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/ku03czt4_491956104_122224574024197143_6634209203154252260_n.jpg',
    captionFr: 'Linéaires partenaires — variété complète pour le consommateur',
    captionEn: 'Partner aisles — full variety for the end consumer',
    captionAr: 'ممرات الشركاء — تنوع كامل للمستهلك النهائي',
    city: 'Gabès',
  },
];

// Partner benefits mirror /#partenaires content (single source of truth).
const CONTENT = {
  fr: {
    badge: 'Réseau de partenaires Olion',
    heroTitle: 'Ouvrez votre Point de Vente Olion',
    heroSubtitle:
      "Rejoignez plus de 200 partenaires en Tunisie. Nous vous accompagnons de A à Z pour lancer votre boutique sous l'enseigne Olion — clé en main.",
    heroCta: 'Devenir partenaire',
    heroCtaSecondary: 'Voir la galerie',
    stats: [
      { value: '200+', label: 'Partenaires actifs' },
      { value: '24', label: 'Gouvernorats couverts' },
      { value: '70+', label: 'Références disponibles' },
      { value: '24h', label: 'Réponse commerciale' },
    ],
    gallerySection: {
      kicker: 'Galerie',
      title: 'Nos partenaires en action',
      subtitle:
        "Découvrez des points de vente réels lancés grâce à notre programme partenariat — PLV fournie, branding complet, équipement installé.",
    },
    benefitsSection: {
      kicker: 'Programme partenaire',
      title: 'Ce que nous mettons à votre disposition',
      subtitle:
        "Un accompagnement structuré, dès l'ouverture et au quotidien, pour que votre boutique Olion soit rentable rapidement.",
    },
    benefits: [
      { icon: Store, title: 'Ouverture clé-en-main', desc: "Enseigne, façade, étagères, PLV : nous installons votre boutique selon notre charte." },
      { icon: TrendingUp, title: 'Marges attractives', desc: 'Tarifs revendeur compétitifs sur toute la gamme retail & vrac.' },
      { icon: Truck, title: 'Livraison gratuite', desc: "Livraison offerte dès le seuil minimum d'approvisionnement." },
      { icon: BarChart3, title: 'Étude de marché', desc: 'Analyse de votre zone de chalandise avant ouverture.' },
      { icon: MapPin, title: 'Exclusivité territoriale', desc: 'Implantation optimale, sans cannibalisation entre partenaires.' },
      { icon: Megaphone, title: 'Marketing & PLV', desc: 'Affiches, banderoles, drapeaux, supports digitaux — fournis.' },
      { icon: Headphones, title: 'Commercial dédié', desc: 'Un référent Olion en contact direct, 7j/7.' },
      { icon: Target, title: 'Formation continue', desc: 'Produits, techniques de vente, conseils en merchandising.' },
      { icon: Gift, title: 'Offres promotionnelles', desc: 'Accès aux promotions saisonnières et exclusivités revendeur.' },
    ],
    ctaTitle: "Prêt à devenir partenaire Olion ?",
    ctaDesc: "Remplissez notre formulaire — notre équipe commerciale vous rappelle sous 24 heures avec une étude personnalisée de votre zone.",
    ctaButton: 'Demander un partenariat',
    ctaContact: 'ou appelez-nous : ',
  },
  en: {
    badge: 'Olion partner network',
    heroTitle: 'Open your Olion Point of Sale',
    heroSubtitle:
      'Join 200+ partners across Tunisia. We support you from A to Z to launch your store under the Olion brand — fully turnkey.',
    heroCta: 'Become a partner',
    heroCtaSecondary: 'View the gallery',
    stats: [
      { value: '200+', label: 'Active partners' },
      { value: '24', label: 'Governorates covered' },
      { value: '70+', label: 'References available' },
      { value: '24h', label: 'Sales response time' },
    ],
    gallerySection: {
      kicker: 'Gallery',
      title: 'Our partners in action',
      subtitle:
        'Discover real points of sale launched thanks to our partnership program — POS displays, full branding and equipment installed.',
    },
    benefitsSection: {
      kicker: 'Partner program',
      title: 'What we put at your disposal',
      subtitle:
        'Structured support from day one and every day after, so your Olion store becomes profitable quickly.',
    },
    benefits: [
      { icon: Store, title: 'Turnkey opening', desc: 'Sign, storefront, shelves, POS materials: we set up your shop to our brand standards.' },
      { icon: TrendingUp, title: 'Attractive margins', desc: 'Competitive reseller pricing on the entire retail & bulk range.' },
      { icon: Truck, title: 'Free delivery', desc: 'Free delivery from the minimum order threshold.' },
      { icon: BarChart3, title: 'Market study', desc: 'Analysis of your catchment area before opening.' },
      { icon: MapPin, title: 'Territorial exclusivity', desc: 'Optimal placement, no cannibalisation between partners.' },
      { icon: Megaphone, title: 'Marketing & POS', desc: 'Posters, banners, flags, digital media — all provided.' },
      { icon: Headphones, title: 'Dedicated sales rep', desc: 'A direct Olion contact, 7 days a week.' },
      { icon: Target, title: 'Continuous training', desc: 'Products, sales techniques, merchandising advice.' },
      { icon: Gift, title: 'Promotional offers', desc: 'Access to seasonal promotions and reseller-exclusive deals.' },
    ],
    ctaTitle: 'Ready to become an Olion partner?',
    ctaDesc:
      'Fill out our form — our sales team will call you back within 24 hours with a personalised study of your area.',
    ctaButton: 'Request a partnership',
    ctaContact: 'or call us: ',
  },
  ar: {
    badge: 'شبكة شركاء أوليون',
    heroTitle: 'افتح نقطة بيع أوليون الخاصة بك',
    heroSubtitle:
      'انضم إلى أكثر من 200 شريك في تونس. ندعمك من الألف إلى الياء لإطلاق متجرك تحت علامة أوليون — تشغيل كامل.',
    heroCta: 'كن شريكاً',
    heroCtaSecondary: 'عرض المعرض',
    stats: [
      { value: '+200', label: 'شريك نشط' },
      { value: '24', label: 'ولاية مغطاة' },
      { value: '+70', label: 'مرجع متاح' },
      { value: '24 ساعة', label: 'سرعة الرد التجاري' },
    ],
    gallerySection: {
      kicker: 'المعرض',
      title: 'شركاؤنا على أرض الواقع',
      subtitle:
        'اكتشف نقاط بيع حقيقية أُطلقت بفضل برنامج الشراكة — لافتات، هوية كاملة، وتجهيزات مركبة.',
    },
    benefitsSection: {
      kicker: 'برنامج الشركاء',
      title: 'ما نضعه في تصرفك',
      subtitle: 'مرافقة منظمة، منذ يوم الافتتاح وكل يوم بعده، لتحقيق ربحية سريعة لمتجرك أوليون.',
    },
    benefits: [
      { icon: Store, title: 'افتتاح جاهز للتشغيل', desc: 'لافتة، واجهة، رفوف، شاشات بيع: نقوم بإعداد متجرك وفق هويتنا.' },
      { icon: TrendingUp, title: 'هوامش جذابة', desc: 'أسعار بائع تنافسية على مجموعة التجزئة والجملة بالكامل.' },
      { icon: Truck, title: 'توصيل مجاني', desc: 'توصيل مجاني ابتداءً من الحد الأدنى للطلب.' },
      { icon: BarChart3, title: 'دراسة السوق', desc: 'تحليل لمنطقة جذبك قبل الافتتاح.' },
      { icon: MapPin, title: 'حصرية إقليمية', desc: 'تموضع مثالي، دون تنافس بين الشركاء.' },
      { icon: Megaphone, title: 'التسويق ولوازم البيع', desc: 'ملصقات، لافتات، أعلام، وسائط رقمية — جميعها مُقدّمة.' },
      { icon: Headphones, title: 'مندوب مبيعات مخصص', desc: 'اتصال مباشر مع فريق أوليون، 7 أيام في الأسبوع.' },
      { icon: Target, title: 'تدريب مستمر', desc: 'منتجات، تقنيات بيع، نصائح في عرض البضائع.' },
      { icon: Gift, title: 'عروض ترويجية', desc: 'وصول للعروض الموسمية وامتيازات حصرية للموزعين.' },
    ],
    ctaTitle: 'هل أنت مستعد لتصبح شريك أوليون؟',
    ctaDesc: 'املأ النموذج — سيتصل بك فريقنا التجاري خلال 24 ساعة مع دراسة شخصية لمنطقتك.',
    ctaButton: 'اطلب الشراكة',
    ctaContact: 'أو اتصل بنا: ',
  },
};

const PHONE_DISPLAY = '+216 99 804 002';

// Lightbox modal — pure JSX, no extra deps.
const Lightbox = ({ photo, onClose, language, direction }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!photo) return null;
  const caption =
    language === 'ar' ? photo.captionAr : language === 'en' ? photo.captionEn : photo.captionFr;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="pos-lightbox"
      dir={direction}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
        aria-label="Close"
        data-testid="pos-lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>
      <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={photo.src}
          alt={caption}
          className="w-full max-h-[80vh] object-contain rounded-xl shadow-2xl"
        />
        <figcaption className="text-center text-white/90 text-sm mt-4 max-w-3xl mx-auto">
          {photo.city && (
            <span className="inline-flex items-center gap-1 bg-orange-500 text-white text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full mr-2 mb-2">
              <MapPin className="w-3 h-3" />
              {photo.city}
            </span>
          )}
          {caption}
        </figcaption>
      </figure>
    </div>
  );
};

export const OlionVracPage = () => {
  const { language, direction } = useLanguage();
  const c = CONTENT[language] || CONTENT.fr;
  const [activePhoto, setActivePhoto] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const localizedCaption = (p) =>
    language === 'ar' ? p.captionAr : language === 'en' ? p.captionEn : p.captionFr;

  return (
    <div dir={direction} className="min-h-screen bg-white">
      {/* Hero */}
      <section
        className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-700 to-orange-500 text-white"
        data-testid="vrac-hero"
      >
        {/* Hero background photo */}
        <img
          src={POS_PHOTOS[0].src}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-overlay"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-blue-950/80 via-blue-900/40 to-transparent" />

        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className={`max-w-3xl ${direction === 'rtl' ? 'ml-auto text-right' : ''}`}>
            <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6 border border-white/20">
              <Sparkles className="w-3.5 h-3.5" />
              {c.badge}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-5">
              {c.heroTitle}
            </h1>
            <p className="text-lg text-white/90 leading-relaxed max-w-2xl">{c.heroSubtitle}</p>

            <div className={`mt-8 flex flex-wrap gap-3 ${direction === 'rtl' ? 'justify-end' : ''}`}>
              <Link
                to="/#partenaires"
                data-testid="vrac-hero-cta"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3.5 rounded-full font-bold text-sm shadow-xl shadow-orange-500/30 transition-all hover:scale-105"
              >
                <Handshake className="w-4 h-4" />
                {c.heroCta}
              </Link>
              <a
                href="#gallery"
                data-testid="vrac-hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3.5 rounded-full font-bold text-sm border border-white/25 transition-all"
              >
                {c.heroCtaSecondary}
                <ArrowRight className={`w-4 h-4 ${direction === 'rtl' ? 'rotate-180' : ''}`} />
              </a>
            </div>

            {/* Stats */}
            <div className={`mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl ${direction === 'rtl' ? 'ml-auto' : ''}`}>
              {c.stats.map((s, i) => (
                <div
                  key={i}
                  className="bg-white/10 backdrop-blur border border-white/20 rounded-xl px-4 py-3"
                >
                  <div className="text-2xl sm:text-3xl font-bold leading-none">{s.value}</div>
                  <div className="text-[11px] uppercase tracking-wider mt-1 text-white/80">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section
        id="gallery"
        className="py-20 bg-gradient-to-b from-white via-gray-50 to-white scroll-mt-24"
        data-testid="vrac-gallery"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`max-w-2xl mb-12 ${direction === 'rtl' ? 'ml-auto text-right' : ''}`}>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">
              {c.gallerySection.kicker}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mt-3 mb-4 leading-tight">
              {c.gallerySection.title}
            </h2>
            <p className="text-gray-600 leading-relaxed">{c.gallerySection.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[220px] gap-4">
            {POS_PHOTOS.map((p, i) => (
              <button
                type="button"
                key={i}
                onClick={() => setActivePhoto(p)}
                data-testid={`pos-photo-${i}`}
                className={`group relative overflow-hidden rounded-2xl bg-gray-100 shadow-md hover:shadow-2xl transition-all duration-300 ${p.span || ''}`}
              >
                <img
                  src={p.src}
                  alt={localizedCaption(p)}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-95 transition-opacity" />
                <div className={`absolute bottom-0 inset-x-0 p-4 ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
                  {p.city && (
                    <span className="inline-flex items-center gap-1 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow-lg mb-2">
                      <MapPin className="w-3 h-3" />
                      {p.city}
                    </span>
                  )}
                  <p className="text-white text-sm font-semibold leading-snug drop-shadow-lg">
                    {localizedCaption(p)}
                  </p>
                </div>
                <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
                  <ArrowRight className="w-4 h-4 text-blue-700" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white" data-testid="vrac-benefits">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`max-w-2xl mb-12 ${direction === 'rtl' ? 'ml-auto text-right' : ''}`}>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
              {c.benefitsSection.kicker}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mt-3 mb-4 leading-tight">
              {c.benefitsSection.title}
            </h2>
            <p className="text-gray-600 leading-relaxed">{c.benefitsSection.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.benefits.map((b, i) => {
              const Icon = b.icon;
              const gradients = [
                'from-blue-600 to-blue-700',
                'from-orange-500 to-orange-600',
                'from-emerald-500 to-teal-600',
                'from-purple-600 to-purple-700',
                'from-pink-500 to-rose-600',
                'from-indigo-500 to-blue-600',
                'from-cyan-500 to-blue-600',
                'from-amber-500 to-orange-600',
                'from-rose-500 to-pink-600',
              ];
              return (
                <article
                  key={i}
                  data-testid={`vrac-benefit-${i}`}
                  className={`group relative bg-white border border-gray-200 hover:border-transparent rounded-2xl p-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 ${direction === 'rtl' ? 'text-right' : ''}`}
                >
                  <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${gradients[i % gradients.length]} opacity-0 group-hover:opacity-[0.04] transition-opacity pointer-events-none`} />
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradients[i % gradients.length]} flex items-center justify-center shadow-lg mb-4 ${direction === 'rtl' ? 'ml-auto' : ''}`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 leading-snug">{b.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{b.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pb-20" data-testid="vrac-cta">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-800 via-blue-700 to-orange-500 p-10 sm:p-14 text-white shadow-2xl ${
              direction === 'rtl' ? 'text-right' : ''
            }`}
          >
            <div className="absolute inset-0 opacity-25 pointer-events-none [background-image:radial-gradient(circle_at_20%_30%,white,transparent_40%),radial-gradient(circle_at_85%_70%,white,transparent_45%)]" />
            <div className="relative flex flex-col lg:flex-row items-start lg:items-center gap-8">
              <div className="flex-1">
                <h3 className="text-3xl sm:text-4xl font-bold mb-3 leading-tight">{c.ctaTitle}</h3>
                <p className="text-white/90 text-base sm:text-lg max-w-2xl">{c.ctaDesc}</p>
              </div>
              <div className={`flex flex-col gap-3 shrink-0 ${direction === 'rtl' ? 'items-end' : ''}`}>
                <Link
                  to="/#partenaires"
                  data-testid="vrac-cta-button"
                  className="inline-flex items-center gap-2 bg-white text-blue-800 hover:bg-gray-100 px-7 py-4 rounded-full font-bold text-sm sm:text-base shadow-xl transition-all hover:scale-105 whitespace-nowrap"
                >
                  <Handshake className="w-5 h-5" />
                  {c.ctaButton}
                  <ArrowRight className={`w-4 h-4 ${direction === 'rtl' ? 'rotate-180' : ''}`} />
                </Link>
                <p className="text-xs text-white/80">
                  {c.ctaContact}
                  <a href={`tel:${PHONE_DISPLAY.replace(/\s/g, '')}`} className="font-bold underline">
                    {PHONE_DISPLAY}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Lightbox
        photo={activePhoto}
        onClose={() => setActivePhoto(null)}
        language={language}
        direction={direction}
      />
    </div>
  );
};
